<template>
  <el-dialog
    v-model="visible"
    title="处理售后"
    width="600px"
    :before-close="handleClose"
    :close-on-click-modal="false"
  >
    <el-form
      ref="formRef"
      :model="formData"
      :rules="rules"
      label-width="100px"
      @submit.prevent
    >
      <!-- 订单信息 -->
      <el-card class="order-info" shadow="never">
        <template #header>
          <span>订单信息</span>
        </template>
        <div class="info-row">
          <span class="label">订单编号：</span>
          <span class="value">{{ orderData.id || '无' }}</span>
        </div>
        <div class="info-row">
          <span class="label">订单名称：</span>
          <span class="value">{{ orderData.name || '无' }}</span>
        </div>
        <div class="info-row">
          <span class="label">实际价格：</span>
          <span class="value">{{ orderData.realPrice || orderData.totalPrice || '0' }}元</span>
        </div>
        <div class="info-row">
          <span class="label">售后原因：</span>
          <span class="value">{{ orderData.reason || '无' }}</span>
        </div>
      </el-card>

      <!-- 处理信息 -->
      <el-form-item label="总计退款金额" prop="backPrice" class="form-item">
        <el-input-number
          v-model="formData.backPrice"
          :min="0"
          :max="parseFloat(orderData.realPrice || orderData.totalPrice || '0')"
          :precision="2"
          controls-position="right"
          style="width: 200px"
        />
        <span class="input-suffix">元（最大可退：{{ orderData.realPrice || orderData.totalPrice || '0' }}元）</span>
      </el-form-item>

      <el-form-item label="运单号" prop="postcode" class="form-item">
        <el-input
          v-model="formData.postcode"
          placeholder="请输入运单号"
          clearable
          maxlength="50"
          show-word-limit
        />
      </el-form-item>

      <el-form-item label="处理结果" prop="processResult" class="form-item">
        <el-input
          v-model="formData.processResult"
          type="textarea"
          :rows="4"
          placeholder="请详细说明处理结果和原因"
          maxlength="500"
          show-word-limit
        />
      </el-form-item>
    </el-form>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="handleClose">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">
          确认处理
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts" setup>
import { ref, watch, reactive, computed } from 'vue';
import { ElMessage, type FormInstance, type FormRules } from 'element-plus';
import { useApi } from '@/hooks/useApi';

const props = defineProps<{
  modelValue: boolean;
  orderData: any;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'success': [];
}>();

const visible = ref(false);
const formRef = ref<FormInstance>();
const submitting = ref(false);

// 表单数据
const formData = reactive({
  backPrice: 0,
  postcode: '',
  processResult: '',
});

// 表单验证规则
const rules = computed((): FormRules => ({
  backPrice: [
    {
      validator: (rule: any, value: number, callback: any) => {
        if (value === undefined || value === null) {
          callback(new Error('请输入退款金额'));
        } else if (value < 0) {
          callback(new Error('退款金额不能为负数'));
        } else if (value > parseFloat(props.orderData.realPrice || props.orderData.totalPrice || '0')) {
          callback(new Error('退款金额不能超过实际价格'));
        } else {
          callback();
        }
      },
      trigger: 'blur',
    },
  ],
  processResult: [
    { required: true, message: '请输入处理结果', trigger: 'blur' },
    { min: 10, max: 500, message: '处理结果长度应在10-500个字符之间', trigger: 'blur' },
  ],
}));

// 重置表单
const resetForm = () => {
  formData.backPrice = parseFloat(props.orderData.backPrice || '0');
  formData.postcode = props.orderData.postcode || '';
  formData.processResult = '';
  formRef.value?.clearValidate();
};

// 监听props变化
watch(() => props.modelValue, (newVal) => {
  visible.value = newVal;
  if (newVal) {
    // 重置表单
    resetForm();
  }
}, { immediate: true });

// 监听visible变化
watch(visible, (newVal) => {
  emit('update:modelValue', newVal);
});

// 关闭弹窗
const handleClose = () => {
  visible.value = false;
};

// 提交处理
const handleSubmit = async() => {
  if (!formRef.value) {
    return;
  }

  try {
    const valid = await formRef.value.validate();
    if (!valid) {
      return;
    }

    submitting.value = true;

    await useApi().billApi.processAfterSalesbill({
      billId: props.orderData.id,
      processResult: formData.processResult,
      backPrice: String(formData.backPrice),
      postcode: formData.postcode,
    });

    ElMessage.success('售后处理成功');
    emit('success');
    handleClose();
  } catch (error) {
    ElMessage.error('售后处理失败，请重试');
    console.error('售后处理失败:', error);
  } finally {
    submitting.value = false;
  }
};
</script>

<style lang="scss" scoped>
.order-info {
  margin-bottom: 20px;

  .info-row {
    display: flex;
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }

    .label {
      width: 80px;
      color: #606266;
      font-weight: 500;
    }

    .value {
      flex: 1;
      color: #303133;
    }
  }
}

.form-item {
  margin-bottom: 20px;
}

.input-suffix {
  margin-left: 10px;
  color: #909399;
  font-size: 14px;
}

.dialog-footer {
  text-align: right;
}
</style>
